// The whole game: draw, pass, guess, pass, draw.
//
// Matt's brief (2026-08-27): "the actual rules of Telestrations are
// irrelevant - I think in the real game there's some way to win, there's
// points, I don't care about any of that. All we need to do is implement the
// draw, pass, write, pass, draw loop and it should be able to work for any
// number of players, as many as we want."
//
// So there is no score here, and there are no word cards. Step one is
// somebody typing whatever they feel like.

export const MIN_PLAYERS = 3;

// A ceiling on seats, so bots can't be added forever. Twelve is the big box.
export const MAX_PLAYERS = 12;

// The big-box sketchbook is 12 pages, which is the length Matt asked for.
export const MAX_PAGES = 12;

export const STEP_KINDS = { WORD: 'word', DRAW: 'draw', GUESS: 'guess' };

export const PHASES = { LOBBY: 'lobby', PLAYING: 'playing', REVEAL: 'reveal' };

/**
 * How many pages a book gets: always twelve, like the big box.
 *
 * Books pass one seat per step, so with fewer than twelve players a book laps
 * and comes back round to the person who started it. That is deliberate.
 *
 * The first version capped this at the player count to stop exactly that, on
 * the theory that being handed your own drawing to guess ruins it. Matt's
 * call, and he's right: "I don't mind if a book makes more than one lap or
 * passes by the person who started it, because there's no way they're gonna
 * actually identify it by the time it gets back around to them." Four rounds
 * of mangling later, your own idea is unrecognisable - and a three-player game
 * that ran only three pages was over before it got funny.
 *
 * `playerCount` is still taken so callers don't all have to change if this
 * ever depends on the table again.
 */
// eslint-disable-next-line no-unused-vars
export const pageCount = (playerCount) => MAX_PAGES;

/**
 * How many times each book goes right round the table. Shown in the lobby, so
 * a small group knows what it's signing up for before it starts.
 */
export const lapsFor = (playerCount) => (
  playerCount > 0 ? MAX_PAGES / playerCount : 0
);

/**
 * What kind of thing is page `index` (0-based)?
 * Page 0 is the starting word; then it alternates drawing, guess, drawing...
 */
export const kindOfPage = (index) => {
  if (index === 0) return STEP_KINDS.WORD;
  return index % 2 === 1 ? STEP_KINDS.DRAW : STEP_KINDS.GUESS;
};

/**
 * Which book is seat `seat` holding on step `step`?
 *
 * Books move one seat "left" per step and every book moves at once, so this
 * is pure arithmetic - there is no passing to simulate. Returns the seat
 * index of the book's OWNER, which is also the book's id.
 */
export const bookHeldBy = (seat, step, playerCount) => (
  ((seat - step) % playerCount + playerCount) % playerCount
);

/** The inverse: who is holding book `book` on step `step`? */
export const holderOfBook = (book, step, playerCount) => (
  (book + step) % playerCount
);

/**
 * The page a holder needs to see to do their bit: the previous page of the
 * book they're now holding. Null on step 0, where there is nothing to react
 * to yet.
 */
export const promptPageIndex = (step) => (step === 0 ? null : step - 1);

/**
 * Everything a seat needs to render its turn on a given step.
 */
export const assignmentFor = (state, seat) => {
  const playerCount = state.players.length;
  const { step } = state;
  const book = bookHeldBy(seat, step, playerCount);
  const kind = kindOfPage(step);
  const promptIndex = promptPageIndex(step);
  const prompt = promptIndex === null ? null : state.books[book]?.pages?.[promptIndex] ?? null;
  return { book, kind, step, prompt };
};

/**
 * Has this seat done its bit for the current step?
 */
export const hasSubmitted = (state, seat) => {
  const book = bookHeldBy(seat, state.step, state.players.length);
  return Boolean(state.books[book]?.pages?.[state.step]);
};

/**
 * Everyone who still owes the table a page this step.
 *
 * Only counts players who are actually connected: a game shouldn't sit
 * forever waiting on a closed laptop, and the caller decides what to do about
 * the ones who are gone.
 */
export const waitingOn = (state, presence = null) => state.players
  // A bot is never "away" - it has no connection to lose, and the driver
  // plays it. Filtering bots out by presence would let a step advance before
  // they'd drawn anything, leaving holes all through their books.
  .filter((player) => player.isBot || (presence ? presence[player.id] : true))
  .filter((player) => !hasSubmitted(state, player.seat));

export const everyoneDone = (state, presence = null) => waitingOn(state, presence).length === 0;
